import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Calendar, Clock, CheckCircle, AlertTriangle, Eye, Search,
  DollarSign, User, ArrowLeft, FileText, Download, Upload, Filter,
  ChevronDown, RefreshCw, Building2, Plus, Wallet, Percent, CreditCard,
  X, Loader, Save, CheckSquare, XSquare, Copy
} from 'lucide-react';
import { formatCurrency, formatDate, custodianAPI } from '../../services/api';

const PayoutManagement = () => {
  const navigate = useNavigate();
  const [selectedBond, setSelectedBond] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUploadPaymentModal, setShowUploadPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [profitNotes, setProfitNotes] = useState('');

  // Upload Payment specific states
  const [paymentFile, setPaymentFile] = useState(null);
  const [selectedPaymentForUpload, setSelectedPaymentForUpload] = useState(null);

  // CSV upload states
  const [uploadedFile, setUploadedFile] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvStats, setCsvStats] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [calculatedTotalAmount, setCalculatedTotalAmount] = useState(null);

  // Track uploaded data for each bond
  const [bondUploadData, setBondUploadData] = useState({});

  // Real data states
  const [custodianPayments, setCustodianPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [bondTypeFilter, setBondTypeFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');

  // Tab system states
  const [activeView, setActiveView] = useState('list');

  // Approval states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedPaymentForApproval, setSelectedPaymentForApproval] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionComments, setRejectionComments] = useState('');

  // Pending approvals data
  const [pendingApprovalsData, setPendingApprovalsData] = useState([]);
  const [pendingApprovalsLoading, setPendingApprovalsLoading] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  // Sub-investor payment details modal states
  const [showSubInvestorPaymentDetailsModal, setShowSubInvestorPaymentDetailsModal] = useState(false);
  const [selectedPayoutForDetails, setSelectedPayoutForDetails] = useState(null);
  const [subInvestorPayments, setSubInvestorPayments] = useState([]);
  const [loadingSubInvestorPayments, setLoadingSubInvestorPayments] = useState(false);

  // Fetch custodian payments from API
  const fetchCustodianPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await custodianAPI.getPayments();

      if (response.data.success) {
        setCustodianPayments(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch custodian payments');
      }
    } catch (error) {
      console.error('Error fetching custodian payments:', error);
      setError(error.message);
      setCustodianPayments([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchCustodianPayments();
    fetchPendingApprovalsCount();
  }, []);

  // Fetch pending approvals when switching to approvals tab
  useEffect(() => {
    if (activeView === 'approvals') {
      fetchPendingApprovals();
    }
  }, [activeView]);

  // Handle viewing sub-investor payment details
  const handleViewSubInvestorPaymentDetails = async (payment) => {
    try {
      // Validate payment object and ID
      const payoutId = payment._id || payment.id;
      if (!payment || !payoutId) {
        console.error('Invalid payment object or missing ID:', payment);
        alert('Invalid payment data. Please try again.');
        return;
      }
      
      setSelectedPayoutForDetails(payment);
      setLoadingSubInvestorPayments(true);
      setShowSubInvestorPaymentDetailsModal(true);
      
      console.log('Fetching sub-investor payments for payout ID:', payoutId);
      const response = await custodianAPI.getSubInvestorPayments(payoutId);
      
      if (response.data.success) {
        setSubInvestorPayments(response.data.subInvestorPayments);
      } else {
        console.error('Failed to fetch sub-investor payment details:', response.data.message);
        setSubInvestorPayments([]);
      }
    } catch (error) {
      console.error('Error fetching sub-investor payment details:', error);
      setSubInvestorPayments([]);
    } finally {
      setLoadingSubInvestorPayments(false);
    }
  };

  // Fetch pending approvals count
  const fetchPendingApprovalsCount = async () => {
    try {
      const response = await custodianAPI.getPendingPaymentApprovals({ limit: 1 });

      if (response.data.success) {
        // Get the total count from the response if available, otherwise use the data length
        setPendingApprovalsCount(response.data.data.length);
      } else {
        console.error('Failed to fetch pending approvals count:', response.data.message);
        setPendingApprovalsCount(0);
      }
    } catch (error) {
      console.error('Error fetching pending approvals count:', error);
      setPendingApprovalsCount(0);
    }
  };

  // Fetch pending payment approvals
  const fetchPendingApprovals = async () => {
    try {
      setPendingApprovalsLoading(true);
      const response = await custodianAPI.getPendingPaymentApprovals();

      if (response.data.success) {
        setPendingApprovalsData(response.data.data);
        setPendingApprovalsCount(response.data.data.length);
      } else {
        console.error('Failed to fetch pending approvals:', response.data.message);
        setPendingApprovalsData([]);
        setPendingApprovalsCount(0);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      setPendingApprovalsData([]);
      setPendingApprovalsCount(0);
    } finally {
      setPendingApprovalsLoading(false);
    }
  };

  // Handle L2 approval
  const handleApproveL2 = (payment) => {
    setSelectedPaymentForApproval(payment);
    setApprovalComments('');
    setShowApprovalModal(true);
  };

  // Confirm L2 approval
  const confirmApproveL2 = async () => {
    if (!selectedPaymentForApproval) return;

    try {
      const response = await custodianAPI.approvePaymentL2(selectedPaymentForApproval._id, {
        comments: approvalComments
      });

      if (response.data.success) {
        alert('Payment distribution L2 approval granted successfully!');
        setShowApprovalModal(false);
        setSelectedPaymentForApproval(null);
        setApprovalComments('');

        // Refresh data
        await fetchCustodianPayments();
        await fetchPendingApprovalsCount();
        if (activeView === 'approvals') {
          await fetchPendingApprovals();
        }
      } else {
        alert('Failed to approve L2: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error approving L2:', error);
      alert('Error approving L2: ' + error.message);
    }
  };

  // Handle rejection
  const handleRejectPayment = (payment) => {
    setSelectedPaymentForApproval(payment);
    setRejectionReason('');
    setRejectionComments('');
    setShowRejectionModal(true);
  };

  // Confirm rejection
  const confirmRejectPayment = async () => {
    if (!selectedPaymentForApproval || !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      const response = await custodianAPI.rejectPayment(selectedPaymentForApproval._id, {
        rejectionReason: rejectionReason,
        comments: rejectionComments
      });

      if (response.data.success) {
        alert('Payment distribution rejected successfully!');
        setShowRejectionModal(false);
        setSelectedPaymentForApproval(null);
        setRejectionReason('');
        setRejectionComments('');

        // Refresh data
        await fetchCustodianPayments();
        await fetchPendingApprovalsCount();
        if (activeView === 'approvals') {
          await fetchPendingApprovals();
        }
      } else {
        alert('Failed to reject payment: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert('Error rejecting payment: ' + error.message);
    }
  };

  // Get approval status badge
  const getApprovalStatusBadge = (approvalStatus) => {
    switch (approvalStatus) {
      case 'pending_l1':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending L1</span>;
      case 'pending_l2':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Pending L2</span>;
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Approved</span>;
      case 'rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Unknown</span>;
    }
  };

  // Helper function to get issuer display name from populated data
  const getIssuerDisplayName = (issuer) => {
    if (!issuer) return 'Unknown Issuer';

    // If issuer is still a string (not populated), return it directly
    if (typeof issuer === 'string') return issuer;

    // If issuer is an object with populated User data
    if (typeof issuer === 'object') {
      // Check for company name first (for institutional users)
      if (issuer.companyName) return issuer.companyName;

      // Check for full name (firstName + lastName)
      if (issuer.firstName && issuer.lastName) {
        return `${issuer.firstName} ${issuer.lastName}`;
      }

      // Check for single name field
      if (issuer.name) return issuer.name;

      // Fallback to email
      if (issuer.email) return issuer.email;
    }

    return 'Unknown Issuer';
  };

  // Transform API data to match the expected format
  const transformedPayments = useMemo(() => {
    return custodianPayments.map(payment => ({
      id: payment._id,
      bondId: payment.bondId?.isin || payment.bondId?._id,
      bondName: payment.bondId?.bondName || 'Unknown Bond',
      issuer: getIssuerDisplayName(payment.bondId?.issuer),
      bondType: payment.bondId?.productType === 'Sukuk' ? 'Sukuk' : 'Conventional Bonds',
      paymentDate: payment.paymentDate,
      paymentType: payment.paymentType === 'Coupon' ? 'Coupon' :
        payment.paymentType === 'Profit Distribution' ? 'Profit' :
          payment.paymentType === 'Principal + Coupon' ? 'Principal + Coupon' :
            payment.paymentType === 'Principal + Profit' ? 'Principal + Profit' : payment.paymentType,
      totalPaymentAmount: payment.actualPaymentAmount || payment.allocatedAmount,
      paymentStatus: payment.paymentStatus,
      numberOfSubInvestors: payment.subInvestorPayments?.length || 0,
      couponRate: payment.bondId?.couponRate,
      profitRate: payment.bondId?.profitRate,
      principalOutstanding: payment.bondId?.issueAmount || 0,
      isFunded: payment.paymentStatus === 'Ready for Distribution',
      currency: payment.currency || 'MYR',
      maturityDate: payment.bondId?.maturityDate,
      issueDate: payment.bondId?.issueDate,
      status: 'active',
      isin: payment.bondId?.isin,
      // Additional fields for the new workflow
      allocatedAmount: payment.allocatedAmount,
      actualPaymentAmount: payment.actualPaymentAmount,
      remainingAmount: payment.remainingAmount, // Remaining amount after partial uploads
      mainPaymentId: payment.mainPaymentId?._id,
      subInvestorPayments: payment.subInvestorPayments || [],
      uploadedAt: payment.uploadedAt,
      processedAt: payment.processedAt,
      // Approval workflow fields
      approvalStatus: payment.approvalStatus,
      l1ApprovedBy: payment.l1ApprovedBy,
      l2ApprovedBy: payment.l2ApprovedBy,
      l1ApprovedAt: payment.l1ApprovedAt,
      l2ApprovedAt: payment.l2ApprovedAt
    }));
  }, [custodianPayments]);

  // Filter and search logic
  const filteredPayments = useMemo(() => {
    return transformedPayments.filter(payment => {
      // Only exclude payments that are pending L2 approval (not approved or rejected)
      if (payment.approvalStatus === 'pending_l2') {
        return false;
      }

      const matchesSearch =
        payment.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (payment.isin && payment.isin.toLowerCase().includes(searchTerm.toLowerCase())) ||
        payment.issuer.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesBondType = bondTypeFilter === 'all' || payment.bondType === bondTypeFilter;
      const matchesStatus = paymentStatusFilter === 'all' || payment.paymentStatus === paymentStatusFilter;

      return matchesSearch && matchesBondType && matchesStatus;
    });
  }, [transformedPayments, searchTerm, bondTypeFilter, paymentStatusFilter]);

  const getStatusBadge = (status, approvalStatus) => {
    // If payment is approved, show approval status instead of payment status
    if (approvalStatus === 'approved') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckSquare className="w-3 h-3 mr-1" />
          L2 Approved
        </span>
      );
    }

    if (approvalStatus === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <X className="w-3 h-3 mr-1" />
          Rejected
        </span>
      );
    }

    const statusConfig = {
      'Pending FA Payment': { color: 'warning', icon: Clock, text: 'Pending FA Payment' },
      'Ready for Distribution': { color: 'success', icon: CheckCircle, text: 'Ready for Distribution' },
      'In Progress': { color: 'info', icon: AlertTriangle, text: 'In Progress' },
      'Completed': { color: 'success', icon: CheckCircle, text: 'Completed' },
      'Failed': { color: 'error', icon: X, text: 'Failed' }
    };

    const config = statusConfig[status] || { color: 'secondary', icon: FileText, text: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const getPaymentAmountDisplay = (payment) => {
    const initialAmount = payment.actualPaymentAmount || payment.allocatedAmount;
    const remainingAmount = payment.remainingAmount !== null && payment.remainingAmount !== undefined
      ? payment.remainingAmount
      : null;
    
    // Check if CSV has been uploaded: either remainingAmount exists and differs from initial, or subInvestorPayments exist
    const hasUploaded = (remainingAmount !== null && Math.abs(remainingAmount - initialAmount) > 0.01) ||
                        (payment.subInvestorPayments && payment.subInvestorPayments.length > 0);

    if (payment.paymentStatus === 'Pending FA Payment' || !initialAmount) {
      return <span className="text-gray-500">-</span>;
    }

    // Show both initial and remaining amount if CSV has been uploaded
    if (hasUploaded && remainingAmount !== null) {
      return (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {formatCurrency(initialAmount)}
          </div>
          <div className="text-xs text-gray-500">
            Remaining: {formatCurrency(remainingAmount)}
          </div>
        </div>
      );
    }

    // Show only initial amount if no CSV uploaded yet
    return (
      <div className="text-sm font-medium text-gray-900">
        {formatCurrency(initialAmount)}
      </div>
    );
  };

  const handleUploadSubInvestorPayments = (payment) => {
    // Check if payment has already been uploaded and is pending approval
    if (payment.approvalStatus === 'pending_l2' || payment.approvalStatus === 'approved' || payment.approvalStatus === 'rejected') {
      alert('This payment has already been uploaded and is awaiting approval or has been processed.');
      return;
    }

    // Only allow upload if payment is ready for distribution and has allocated amount
    const hasAmount = payment.allocatedAmount || payment.actualPaymentAmount;
    if (payment.paymentStatus !== 'Ready for Distribution' || !hasAmount) {
      alert('Payment is not ready for distribution. Please wait for FA to upload payment.');
      return;
    }

    setSelectedPaymentForUpload(payment);
    setShowUploadPaymentModal(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file only.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setCsvErrors([]);
    setCsvStats(null);
    setShowPreview(false);
    setAllocations([]);

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    try {
      const text = await file.text();
      console.log('CSV Text:', text);

      const result = await parseCSV(text);
      console.log('Parse Result:', result);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setPaymentFile(file);
      setUploadedFile(file);
      setAllocations(result.data || []);
      setCsvErrors(result.errors || []);
      setCsvStats(result.stats || null);
      setShowPreview((result.errors || []).length === 0);

      console.log('State after parsing:', {
        data: result.data,
        errors: result.errors,
        stats: result.stats,
        showPreview: (result.errors || []).length === 0
      });

    } catch (error) {
      clearInterval(progressInterval);
      console.error('Error processing CSV:', error);
      setCsvErrors(['Failed to read CSV file: ' + error.message]);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const parseCSV = async (csvText) => {
    // Handle empty or invalid CSV text
    if (!csvText || typeof csvText !== 'string') {
      return {
        data: [],
        errors: ['Invalid CSV file: File is empty or corrupted'],
        stats: { totalRows: 0, validRows: 0, totalAmount: 0, totalPercentage: 0 }
      };
    }

    const lines = csvText.trim().split('\n');

    // Handle empty file
    if (lines.length === 0) {
      return {
        data: [],
        errors: ['CSV file is empty'],
        stats: { totalRows: 0, validRows: 0, totalAmount: 0, totalPercentage: 0 }
      };
    }

    // Handle file with only headers or no headers
    if (lines.length === 1) {
      return {
        data: [],
        errors: ['CSV file contains only headers or is empty'],
        stats: { totalRows: 0, validRows: 0, totalAmount: 0, totalPercentage: 0 }
      };
    }

    const headers = lines[0].split(',').map(h => h.trim());

    console.log('CSV Headers:', headers);

    // Required headers for custodian payout management
    const requiredHeaders = [
      'Investor ID', 'Investor Name', 'Payment Amount', 'Wallet Address',
      'Percentage', 'Payment Type', 'Payment Reference', 'Payment Date'
    ];

    const errors = [];
    const data = [];
    let totalAmount = 0;
    let totalPercentage = 0;

    // Validate headers
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      console.log('Missing headers:', missingHeaders);
      errors.push(`Missing required headers: ${missingHeaders.join(', ')}`);
      return {
        data: [],
        errors,
        stats: { totalRows: 0, validRows: 0, totalAmount: 0, totalPercentage: 0 }
      };
    }

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(',').map(v => v.trim());
      if (values.length !== headers.length) {
        errors.push(`Row ${i + 1}: Incorrect number of columns`);
        continue;
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });

      // Validate data
      if (!row['Investor ID'] || !row['Investor Name']) {
        errors.push(`Row ${i + 1}: Missing Investor ID or Name`);
        continue;
      }

      const paymentAmount = parseFloat(row['Payment Amount']);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        errors.push(`Row ${i + 1}: Invalid Payment Amount`);
        continue;
      }

      if (!row['Wallet Address'] || row['Wallet Address'].length < 10) {
        errors.push(`Row ${i + 1}: Invalid Wallet Address`);
        continue;
      }

      const percentage = parseFloat(row['Percentage']);
      if (isNaN(percentage) || percentage <= 0) {
        errors.push(`Row ${i + 1}: Invalid Percentage`);
        continue;
      }

      if (!row['Payment Reference']) {
        errors.push(`Row ${i + 1}: Missing Payment Reference`);
        continue;
      }

      if (!row['Payment Type']) {
        errors.push(`Row ${i + 1}: Missing Payment Type`);
        continue;
      }

      if (!row['Payment Date']) {
        errors.push(`Row ${i + 1}: Missing Payment Date`);
        continue;
      }

      const parsedRow = {
        investorId: row['Investor ID'],
        investorName: row['Investor Name'],
        paymentAmount: paymentAmount,
        walletAddress: row['Wallet Address'],
        allocationPercentage: percentage,
        paymentType: row['Payment Type'],
        paymentReference: row['Payment Reference'],
        paymentDate: row['Payment Date']
      };

      console.log('Parsed row:', parsedRow);
      data.push(parsedRow);

      totalAmount += paymentAmount;
      totalPercentage += percentage;
    }



    if (data.length === 0) {
      errors.push('No valid data rows found in CSV file');
    }

    console.log('🔍 Parsed CSV data before database validation:', data);
    console.log('🔍 Total amount before validation:', totalAmount);
    console.log('🔍 Total percentage before validation:', totalPercentage);

    // Database validation - only if basic validation passes
    if (errors.length === 0 && data.length > 0) {
      try {
        // Get bondId - could be ObjectId, ISIN, or undefined
        const bondId = selectedPaymentForUpload?.bondId?._id || selectedPaymentForUpload?.bondId || 'unknown';

        console.log('🔍 Sending validation request with bondId:', bondId);

        const validationResponse = await custodianAPI.validateInvestorWallet(data, bondId);

        console.log('🔍 Database validation response:', validationResponse);

        if (validationResponse && validationResponse.data && validationResponse.data.results) {
          const dbValidationResults = validationResponse.data.results;
          console.log('🔍 Database validation results:', dbValidationResults);

          // Add database validation errors
          if (dbValidationResults.invalid && Array.isArray(dbValidationResults.invalid)) {
            dbValidationResults.invalid.forEach(invalidRow => {
              if (invalidRow.errors && Array.isArray(invalidRow.errors)) {
                invalidRow.errors.forEach(error => {
                  errors.push(`Row ${invalidRow.rowNumber}: ${error}`);
                });
              }
            });
          }

          // Database validation only checks if investor ID and wallet address exist
          // We keep using the original CSV data, not the validation results
          console.log('🔍 Database validation passed - keeping original CSV data');
        } else {
          errors.push('Invalid response from database validation service');
        }
      } catch (validationError) {
        console.error('Database validation error:', validationError);
        // Don't fail completely if database validation fails - allow basic CSV validation to pass
        // but warn the user
        errors.push('Warning: Database validation failed. Basic CSV validation passed, but investor and wallet data could not be verified. Please check your connection and try again for full validation.');
      }
    }

    const result = {
      data,
      errors,
      stats: {
        totalRows: lines.length - 1,
        validRows: data.length,
        totalAmount: totalAmount || 0,
        totalPercentage: totalPercentage || 0
      }
    };

    console.log('Final parse result:', result);
    return result;
  };

  const handleUploadSubInvestorPaymentsSubmit = async () => {
    if (!paymentFile || !selectedPaymentForUpload) {
      alert('Please upload a CSV file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Read file content
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(paymentFile);
      });

      // Parse CSV
      const result = await parseCSV(text);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.errors.length > 0) {
        setCsvErrors(result.errors);
        if (result.data.length === 0) {
          setAllocations([]);
          setShowPreview(false);
          alert('CSV contains errors. Please fix them and try again.');
          return;
        }
      }

      setAllocations(result.data);
      setCsvStats(result.stats);
      setShowPreview(result.errors.length === 0);
      setCsvErrors(result.errors);

      // Validate total payment amount doesn't exceed remaining amount (partial amounts allowed)
      const expectedAmount = selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount;
      const currentRemainingAmount = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
        ? selectedPaymentForUpload.remainingAmount
        : expectedAmount;
      const csvTotalAmount = result.stats.totalAmount;

      console.log('Payment amount validation:', {
        expectedAmount,
        currentRemainingAmount,
        csvTotalAmount,
        difference: currentRemainingAmount - csvTotalAmount
      });

      // Check if uploaded amount exceeds the remaining amount (partial amounts are allowed)
      if (csvTotalAmount > currentRemainingAmount + 0.01) {
        const errorMessage = `Total payment amount in CSV (${formatCurrency(csvTotalAmount)}) cannot exceed the remaining amount (${formatCurrency(currentRemainingAmount)}). Partial amounts are allowed, but the total cannot exceed the remaining balance.`;

        setCsvErrors([...result.errors, errorMessage]);
        setShowPreview(false);
        alert(errorMessage);
        return;
      }

      // Upload to API
      if (result.data.length > 0) {
        const uploadData = {
          subInvestorPayments: result.data
        };

        const response = await custodianAPI.uploadSubInvestorPayments(
          selectedPaymentForUpload.id,
          uploadData
        );

        if (response.data.success) {
          alert('Sub-investor payments uploaded successfully!');
          setShowUploadPaymentModal(false);
          setSelectedPaymentForUpload(null);
          setPaymentFile(null);
          setAllocations([]);
          setCsvErrors([]);
          setCsvStats(null);
          setShowPreview(false);

          // Refresh data
          await fetchCustodianPayments();
        } else {
          alert('Failed to upload sub-investor payments: ' + response.data.message);
        }
      }

    } catch (error) {
      console.error('Error uploading sub-investor payments:', error);
      alert('Error uploading sub-investor payments: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadTemplate = () => {
    const csvContent = [
      'Investor ID,Investor Name,Payment Amount,Wallet Address,Percentage,Payment Type,Payment Reference,Payment Date',
      '6895e4fb671625f8fa621805,Sub Investor 3,250000,0x1234567890abcdef1234567890abcdef12345678,25.00,Profit Distribution,TXN-2025-001,2025-07-22',
      '689463871e48b343c0e5e99e,Sub Investor 1,150000,0xabcdef1234567890abcdef1234567890abcdef12,15.00,Profit Distribution,TXN-2025-002,2025-07-22',
      '6895545d734c54916db03292,Sub Investor 2,100000,0x567890abcdef1234567890abcdef1234567890ab,10.00,Profit Distribution,TXN-2025-003,2025-07-22'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'custodian_payout_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setBondTypeFilter('all');
    setPaymentStatusFilter('all');
  };

  // Helper function to get L1 Initiator display
  const getL1InitiatorDisplay = (payment) => {
    // Look for L1 approval in approval history
    const l1Approval = payment.approvalHistory?.find(
      history => history.level === 'l1' && history.status === 'approved'
    );

    if (l1Approval) {
      // If approvedBy is populated object, get email from it
      let approverEmail = 'N/A';
      if (l1Approval.approvedBy?.email) {
        approverEmail = l1Approval.approvedBy.email;
      } else if (typeof l1Approval.approvedBy === 'string') {
        // If it's just a user ID, we can't show email, but we can show "User ID: xxx"
        approverEmail = `User ID: ${l1Approval.approvedBy}`;
      }
      
      const approvedAt = l1Approval.approvedAt || l1Approval.updatedAt;
      
      return (
        <div>
          <div className="text-sm text-gray-900">{approverEmail}</div>
          {approvedAt && (
            <div className="text-xs text-gray-500">
              {new Date(approvedAt).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })}
            </div>
          )}
        </div>
      );
    }

    // Fallback to direct fields if available
    if (payment.l1ApprovedBy) {
      let approverEmail = 'N/A';
      if (payment.l1ApprovedBy?.email) {
        approverEmail = payment.l1ApprovedBy.email;
      } else if (typeof payment.l1ApprovedBy === 'string') {
        approverEmail = `User ID: ${payment.l1ApprovedBy}`;
      }
      
      const approvedAt = payment.l1ApprovedAt;
      
      return (
        <div>
          <div className="text-sm text-gray-900">{approverEmail}</div>
          {approvedAt && (
            <div className="text-xs text-gray-500">
              {new Date(approvedAt).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })}
            </div>
          )}
        </div>
      );
    }

    return <div className="text-sm text-gray-500">-</div>;
  };

  // Helper function to get L2 Approver display
  const getL2ApproverDisplay = (payment) => {
    // Look for L2 approval in approval history
    const l2Approval = payment.approvalHistory?.find(
      history => history.level === 'l2' && history.status === 'approved'
    );

    if (l2Approval) {
      // If approvedBy is populated object, get email from it
      let approverEmail = 'N/A';
      if (l2Approval.approvedBy?.email) {
        approverEmail = l2Approval.approvedBy.email;
      } else if (typeof l2Approval.approvedBy === 'string') {
        // If it's just a user ID, we can't show email, but we can show "User ID: xxx"
        approverEmail = `User ID: ${l2Approval.approvedBy}`;
      }
      
      const approvedAt = l2Approval.approvedAt || l2Approval.updatedAt;
      
      return (
        <div>
          <div className="text-sm text-gray-900">{approverEmail}</div>
          {approvedAt && (
            <div className="text-xs text-gray-500">
              {new Date(approvedAt).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })}
            </div>
          )}
        </div>
      );
    }

    // Fallback to direct fields if available
    if (payment.l2ApprovedBy) {
      let approverEmail = 'N/A';
      if (payment.l2ApprovedBy?.email) {
        approverEmail = payment.l2ApprovedBy.email;
      } else if (typeof payment.l2ApprovedBy === 'string') {
        approverEmail = `User ID: ${payment.l2ApprovedBy}`;
      }
      
      const approvedAt = payment.l2ApprovedAt;
      
      return (
        <div>
          <div className="text-sm text-gray-900">{approverEmail}</div>
          {approvedAt && (
            <div className="text-xs text-gray-500">
              {new Date(approvedAt).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })}
            </div>
          )}
        </div>
      );
    }

    return <div className="text-sm text-gray-500">-</div>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600">Loading payment schedules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto text-red-600 mb-4" />
          <p className="text-red-600 mb-4">Error loading payment schedules</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchCustodianPayments}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payout Management</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage payment distributions to sub-investors
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-light-gray rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-xs font-medium text-secondary-600 truncate">Waiting for FA</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {filteredPayments.filter(p => p.paymentStatus === 'Pending FA Payment').length}
                </p>
                <p className="text-xs text-yellow-600 mt-1">Pending payments</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-light-gray rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-xs font-medium text-secondary-600 truncate">Ready to Distribute</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {filteredPayments.filter(p => p.paymentStatus === 'Ready for Distribution' && p.actualPaymentAmount).length}
                </p>
                <p className="text-xs text-green-600 mt-1">Ready for upload</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-light-gray rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-blue-100">
                <AlertTriangle className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-xs font-medium text-secondary-600 truncate">Distribution in Progress</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {filteredPayments.filter(p => p.paymentStatus === 'In Progress').length}
                </p>
                <p className="text-xs text-blue-600 mt-1">Currently processing</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-light-gray rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-xs font-medium text-secondary-600 truncate">Completed</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {filteredPayments.filter(p => p.paymentStatus === 'Completed').length}
                </p>
                <p className="text-xs text-green-600 mt-1">Successfully completed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Search Card - 60% width (3 out of 5 columns) */}
          <div className="card lg:col-span-3">
            <div className="card-body">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search bonds by name, ISIN, or issuer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
          </div>

          {/* Bond Type Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={bondTypeFilter}
                onChange={(e) => setBondTypeFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Types</option>
                <option value="Sukuk">Sukuk</option>
                <option value="Conventional Bonds">Conventional Bonds</option>
              </select>
            </div>
          </div>

          {/* Payment Status Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Status</option>
                <option value="Pending FA Payment">Pending FA Payment</option>
                <option value="Ready for Distribution">Ready for Distribution</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveView('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeView === 'list'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Payment Uploads
            </button>
            <button
              onClick={() => setActiveView('approvals')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeView === 'approvals'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending Approvals
            </button>
          </nav>
        </div>

        {/* Payment Schedules Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-secondary-900">
              {activeView === 'list' ? 'Payment Schedules' : 'Pending Payment Approvals'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {activeView === 'list'
                ? `${filteredPayments.length} payment schedule${filteredPayments.length !== 1 ? 's' : ''} found`
                : `${pendingApprovalsData.length} pending approval${pendingApprovalsData.length !== 1 ? 's' : ''} found`
              }
            </p>
          </div>

          {activeView === 'list' && filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No payment schedules found</h3>
              <p className="text-gray-600">
                {searchTerm || bondTypeFilter !== 'all' || paymentStatusFilter !== 'all'
                  ? 'Try adjusting your filters or search terms.'
                  : 'Payment schedules will appear here once bonds are allocated to you.'}
              </p>
            </div>
          ) : activeView === 'approvals' && pendingApprovalsData.length === 0 ? (
            <div className="text-center py-12">
              <CheckSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No pending approvals found</h3>
              <p className="text-gray-600">
                {pendingApprovalsLoading
                  ? 'Loading pending approvals...'
                  : 'No payment distributions are waiting for your approval.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Bond Details
                    </th>
                    <th className="table-header-cell">
                      Issuer
                    </th>
                    <th className="table-header-cell">
                      Payment Date
                    </th>
                    <th className="table-header-cell">
                      Payment Type
                    </th>
                    <th className="table-header-cell">
                      Total Amount
                    </th>
                    <th className="table-header-cell">
                      L1 Initiator
                    </th>
                    <th className="table-header-cell">
                      L2 Approver
                    </th>
                    <th className="table-header-cell">
                      {activeView === 'list' ? 'Status' : 'Approval Status'}
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(activeView === 'list' ? filteredPayments : pendingApprovalsData).map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {activeView === 'list' ? payment.bondName : (payment.bondId?.bondName || 'N/A')}
                          </div>
                          <div className="text-xs text-gray-400 font-mono">
                            {activeView === 'list' ? payment.isin : (payment.bondId?.isin || 'N/A')}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {activeView === 'list' ? payment.issuer : (payment.bondId?.issuer?.companyName || payment.bondId?.issuer?.name || payment.bondId?.issuer || 'N/A')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(payment.paymentDate)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{payment.paymentType}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentAmountDisplay(payment)}
                        {payment.paymentStatus === 'Ready for Distribution' && (payment.allocatedAmount || payment.actualPaymentAmount) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {activeView === 'list'
                              ? `${payment.numberOfSubInvestors || 0} sub-investor${(payment.numberOfSubInvestors || 0) !== 1 ? 's' : ''}`
                              : `${payment.subInvestorPayments?.length || 0} sub-investor${(payment.subInvestorPayments?.length || 0) !== 1 ? 's' : ''}`
                            }
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getL1InitiatorDisplay(payment)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getL2ApproverDisplay(payment)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {activeView === 'list' ? (
                          getStatusBadge(payment.paymentStatus, payment.approvalStatus)
                        ) : (
                          getApprovalStatusBadge(payment.approvalStatus)
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {activeView === 'list' ? (
                            // My Payment Uploads view
                            <div className="flex space-x-2">
                              {/* Upload Payment Button */}
                              <button
                                onClick={() => handleUploadSubInvestorPayments(payment)}
                                disabled={payment.paymentStatus !== 'Ready for Distribution' || !(payment.allocatedAmount || payment.actualPaymentAmount) || payment.approvalStatus === 'pending_l2' || payment.approvalStatus === 'approved' || payment.approvalStatus === 'rejected'}
                                className="btn-primary btn-sm"
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                Upload Payment
                              </button>
                              
                              {/* View Details Button - Only show if payment is approved */}
                              {payment.approvalStatus === 'approved' && (
                                <button
                                  onClick={() => handleViewSubInvestorPaymentDetails(payment)}
                                  className="text-primary-600 hover:text-primary-700 p-1"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ) : (
                            // Pending Approvals view
                            <>
                              <button
                                onClick={() => handleApproveL2(payment)}
                                className="btn-primary btn-sm"
                              >
                                <CheckSquare className="w-3 h-3 mr-1" />
                                Approve L2
                              </button>
                              <button
                                onClick={() => handleRejectPayment(payment)}
                                className="btn-secondary btn-sm"
                              >
                                <XSquare className="w-3 h-3 mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Upload Payment Modal */}
      {showUploadPaymentModal && selectedPaymentForUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Upload Payment Distribution for {selectedPaymentForUpload.bondName}
                </h3>
                <button
                  onClick={() => {
                    setShowUploadPaymentModal(false);
                    setSelectedPaymentForUpload(null);
                    setPaymentFile(null);
                    setUploadedFile(null);
                    setAllocations([]);
                    setCsvErrors([]);
                    setCsvStats(null);
                    setShowPreview(false);
                    setUploadProgress(0);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Bond Summary */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Bond Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Bond Name:</span>
                    <div className="font-medium">{selectedPaymentForUpload.bondName}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Issuer:</span>
                    <div className="font-medium">{selectedPaymentForUpload.issuer}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Remaining Amount:</span>
                    <div className="font-medium">
                      {formatCurrency(
                        selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                          ? selectedPaymentForUpload.remainingAmount
                          : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount)
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">CSV Total Amount:</span>
                    <div className={`font-medium ${
                      csvStats && (() => {
                        const currentRemaining = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                          ? selectedPaymentForUpload.remainingAmount
                          : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount);
                        return csvStats.totalAmount > currentRemaining + 0.01 ? 'text-red-600' : 'text-green-600';
                      })()
                    }`}>
                      {csvStats ? formatCurrency(csvStats.totalAmount) : '-'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Sub Investors:</span>
                    <div className="font-medium">
                      {csvStats ? csvStats.validRows : (selectedPaymentForUpload?.bondId?.numberOfInvestors || '-')}
                    </div>
                  </div>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Upload Payment Distribution CSV</h4>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <div className="text-sm text-gray-600 mb-4">
                    <span className="font-medium text-blue-600 hover:text-blue-500 cursor-pointer">
                      Click to upload
                    </span>
                    {' '}or drag and drop your CSV file here
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                  >
                    Choose File
                  </label>
                  <p className="text-xs text-gray-500 mt-2">CSV files only, max 5MB</p>

                  {/* CSV Format Instructions */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <h5 className="text-sm font-medium text-blue-800 mb-2">CSV Format Requirements:</h5>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Headers: Investor ID, Investor Name, Payment Amount, Wallet Address, Percentage, Payment Type, Payment Reference, Payment Date</li>
                      <li>• Payment Amount: Numbers only (no commas), e.g., 250000</li>
                      <li>• <strong>Total Payment Amount: Cannot exceed the Remaining Amount shown above (partial amounts are allowed)</strong></li>
                      <li>• Wallet Address: Must be at least 10 characters long</li>
                      <li>• Percentage: Individual allocation percentage for each investor</li>
                      <li>• Payment Type: "Profit Distribution" or "Coupon Payment"</li>
                      <li>• Payment Reference: Transaction ID for payment identification</li>
                      <li>• Payment Date: YYYY-MM-DD format</li>
                      <li>• No empty rows or comment lines in the data</li>
                    </ul>
                    <h5 className="text-sm font-medium text-blue-800 mb-2 mt-3">Database Validation:</h5>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Investor ID must exist in database and be associated with this custodian</li>
                      <li>• Wallet Address must exist in database and be associated with this custodian</li>
                      <li>• Wallet Address must belong to the specified Investor ID</li>
                      <li>• Investor must be active and verified</li>
                    </ul>
                  </div>
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="mt-4">
                    <div className="flex items-center mb-2">
                      <Loader className="h-4 w-4 animate-spin text-blue-600 mr-2" />
                      <span className="text-sm text-gray-600">Processing CSV file...</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* File Info */}
                {uploadedFile && !isUploading && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-blue-600 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-blue-800">{uploadedFile.name}</div>
                        <div className="text-xs text-blue-600">
                          {(uploadedFile.size / 1024).toFixed(1)} KB • Uploaded successfully
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* CSV Errors */}
              {csvErrors.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-red-800 mb-2">
                        {csvErrors.length === 1 ? 'CSV Validation Issue' : `${csvErrors.length} CSV Validation Issues`}
                      </h4>
                      <div className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {csvErrors.map((error, index) => (
                          <div key={index}>• {error}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* CSV Stats */}
              {csvStats && (
                <div className={`mb-6 p-4 border rounded-md ${(() => {
                    const currentRemaining = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                      ? selectedPaymentForUpload.remainingAmount
                      : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount);
                    return csvStats.totalAmount > currentRemaining + 0.01
                      ? 'bg-red-50 border-red-200'
                      : 'bg-green-50 border-green-200';
                  })()}`}>
                  <div className="flex items-start">
                    {(() => {
                      const currentRemaining = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                        ? selectedPaymentForUpload.remainingAmount
                        : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount);
                      return csvStats.totalAmount > currentRemaining + 0.01 ? (
                        <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                      );
                    })()}
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium mb-2 ${(() => {
                          const currentRemaining = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                            ? selectedPaymentForUpload.remainingAmount
                            : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount);
                          return csvStats.totalAmount > currentRemaining + 0.01
                            ? 'text-red-800'
                            : 'text-green-800';
                        })()}`}>
                        CSV Processing Summary
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-xs space-y-1">
                        {(() => {
                          const currentRemaining = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                            ? selectedPaymentForUpload.remainingAmount
                            : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount);
                          const isExceeded = csvStats.totalAmount > currentRemaining + 0.01;
                          return (
                            <>
                              <div className={isExceeded ? 'text-red-700' : 'text-green-700'}>
                                Total Rows: {csvStats.totalRows}
                              </div>
                              <div className={isExceeded ? 'text-red-700' : 'text-green-700'}>
                                Valid Rows: {csvStats.validRows}
                              </div>
                              <div className={isExceeded ? 'text-red-700' : 'text-green-700'}>
                                Total Amount: {formatCurrency(csvStats.totalAmount)}
                              </div>
                              <div className={isExceeded ? 'text-red-700' : 'text-green-700'}>
                                Total Percentage: {(csvStats.totalPercentage || 0).toFixed(2)}%
                              </div>
                              <div className={`col-span-2 font-medium ${isExceeded ? 'text-red-700' : 'text-green-700'}`}>
                                Amount Validation: {
                                  isExceeded
                                    ? `❌ Amount exceeds remaining balance (Remaining: ${formatCurrency(currentRemaining)})`
                                    : `✅ Amount within remaining balance (Remaining: ${formatCurrency(currentRemaining)})`
                                }
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Table */}
              {showPreview && allocations.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Payment Distribution Preview</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Investor ID</th>
                          <th className="table-header-cell">Investor Name</th>
                          <th className="table-header-cell">Payment Amount</th>
                          <th className="table-header-cell">Wallet Address</th>
                          <th className="table-header-cell">Percentage</th>
                          <th className="table-header-cell">Payment Type</th>
                          <th className="table-header-cell">Payment Reference</th>
                          <th className="table-header-cell">Payment Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allocations.map((allocation, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-900">{allocation.investorId}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{allocation.investorName}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">{formatCurrency(allocation.paymentAmount)}</td>
                            <td className="px-3 py-2 text-sm text-gray-500 font-mono">{allocation.walletAddress}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">{(allocation.allocationPercentage || 0).toFixed(2)}%</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{allocation.paymentType}</td>
                            <td className="px-3 py-2 text-sm text-gray-500 font-mono">{allocation.paymentReference}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{allocation.paymentDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={downloadTemplate}
                className="btn-secondary"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowUploadPaymentModal(false);
                    setSelectedPaymentForUpload(null);
                    setPaymentFile(null);
                    setUploadedFile(null);
                    setAllocations([]);
                    setCsvErrors([]);
                    setCsvStats(null);
                    setShowPreview(false);
                    setUploadProgress(0);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <div className="flex flex-col">
                  <button
                    onClick={handleUploadSubInvestorPaymentsSubmit}
                    disabled={
                      !showPreview ||
                      allocations.length === 0 ||
                      csvErrors.length > 0 ||
                      (csvStats && (() => {
                        const currentRemaining = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                          ? selectedPaymentForUpload.remainingAmount
                          : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount);
                        return csvStats.totalAmount > currentRemaining + 0.01;
                      })())
                    }
                    className="btn-primary"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Confirm Upload
                  </button>
                  {csvStats && (() => {
                    const currentRemaining = selectedPaymentForUpload.remainingAmount !== null && selectedPaymentForUpload.remainingAmount !== undefined
                      ? selectedPaymentForUpload.remainingAmount
                      : (selectedPaymentForUpload.actualPaymentAmount || selectedPaymentForUpload.allocatedAmount);
                    return csvStats.totalAmount > currentRemaining + 0.01;
                  })() && (
                    <div className="text-xs text-red-600 mt-1">
                      Amount exceeds remaining balance - fix CSV total to enable upload
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve L2 Modal */}
      {showApprovalModal && selectedPaymentForApproval && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Approve L2 Payment Distribution</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Bond:</strong> {selectedPaymentForApproval.bondName}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Amount:</strong> {formatCurrency(selectedPaymentForApproval.actualPaymentAmount || selectedPaymentForApproval.allocatedAmount)}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Sub-investors:</strong> {selectedPaymentForApproval.subInvestorPayments?.length || 0}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Add any comments about this approval..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedPaymentForApproval(null);
                  setApprovalComments('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproveL2}
                className="btn-primary"
              >
                Approve L2
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Payment Modal */}
      {showRejectionModal && selectedPaymentForApproval && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Payment Distribution</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Bond:</strong> {selectedPaymentForApproval.bondName}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Amount:</strong> {formatCurrency(selectedPaymentForApproval.actualPaymentAmount || selectedPaymentForApproval.allocatedAmount)}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Sub-investors:</strong> {selectedPaymentForApproval.subInvestorPayments?.length || 0}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows="3"
                placeholder="Please provide a reason for rejection..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments (Optional)
              </label>
              <textarea
                value={rejectionComments}
                onChange={(e) => setRejectionComments(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows="2"
                placeholder="Add any additional comments..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setSelectedPaymentForApproval(null);
                  setRejectionReason('');
                  setRejectionComments('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRejectPayment}
                disabled={!rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Investor Payment Details Modal */}
      {showSubInvestorPaymentDetailsModal && selectedPayoutForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Sub-Investor Payment Details - {selectedPayoutForDetails.bondName}
                </h3>
                <button
                  onClick={() => {
                    setShowSubInvestorPaymentDetailsModal(false);
                    setSelectedPayoutForDetails(null);
                    setSubInvestorPayments([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loadingSubInvestorPayments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading payment details...</span>
                </div>
              ) : subInvestorPayments.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Records Found</h3>
                  <p className="text-gray-600">No sub-investor payment records found for this payout.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-blue-600">Total Payments</div>
                      <div className="text-2xl font-bold text-blue-900">{subInvestorPayments.length}</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-green-600">Total Amount</div>
                      <div className="text-2xl font-bold text-green-900">
                        {formatCurrency(subInvestorPayments.reduce((sum, payment) => sum + (payment.paymentAmount || 0), 0))}
                      </div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-purple-600">Conventional</div>
                      <div className="text-2xl font-bold text-purple-900">
                        {subInvestorPayments.filter(p => p.bankAccountType === 'conventional').length}
                      </div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="text-sm font-medium text-orange-600">Islamic</div>
                      <div className="text-2xl font-bold text-orange-900">
                        {subInvestorPayments.filter(p => p.bankAccountType === 'islamic').length}
                      </div>
                    </div>
                  </div>

                  {/* Payment Details Table */}
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">
                            Sub-Investor ID
                          </th>
                          <th className="table-header-cell">
                            Name
                          </th>
                          <th className="table-header-cell">
                            Wallet Address
                          </th>
                          <th className="table-header-cell">
                            Account Type
                          </th>
                          <th className="table-header-cell">
                            Amount
                          </th>
                          <th className="table-header-cell">
                            Allocation %
                          </th>
                          <th className="table-header-cell">
                            Reference
                          </th>
                          <th className="table-header-cell">
                            Payment Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {subInvestorPayments.map((payment) => (
                          <tr key={payment._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.subInvestorId || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.subInvestorName || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span className="font-mono text-xs">
                                  {payment.walletAddress && payment.walletAddress !== '-' 
                                    ? `${payment.walletAddress.slice(0, 10)}...${payment.walletAddress.slice(-8)}`
                                    : '-'
                                  }
                                </span>
                                {payment.walletAddress && payment.walletAddress !== '-' && (
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(payment.walletAddress);
                                      // You could add a toast notification here
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                    title="Copy full address"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                payment.bankAccountType === 'conventional' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : payment.bankAccountType === 'islamic'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {payment.bankAccountType === 'conventional' ? 'Conventional' : 
                                 payment.bankAccountType === 'islamic' ? 'Islamic' : 
                                 payment.bankAccountType || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(payment.paymentAmount || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.allocationPercentage ? `${payment.allocationPercentage}%` : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.paymentReference || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.paymentDate ? formatDate(payment.paymentDate) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowSubInvestorPaymentDetailsModal(false);
                  setSelectedPayoutForDetails(null);
                  setSubInvestorPayments([]);
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PayoutManagement;
